import { useEffect, useState } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getUserActivityDetail } from '@containers/Admin/containers/UserPage/services';
import { mentorRoles, supervisorRoles } from '@shared/constants/roles';
import {
  canOpenRecord,
  getActivityTypeIcon,
  getActivityTypeLabel,
  getActivityRoute,
  getStatusTone,
  getDuration,
  getInitiatorRole,
  hasFeedbackRecord,
  isSupportedType,
  formatDuration,
} from './helpers';
import {
  DetailActions,
  DetailCard,
  DetailFields,
  FeedbackValue,
  DetailHeader,
  DetailMeta,
  DetailTitle,
  DetailType,
  EmptyDetail,
  OpenRecordButton,
  StatusPill,
  TypeIcon,
} from './styles';
import { ActivityDetails, ActivityLog, ActivityLogType } from './types';
import { getEventDayAndTime } from '@utils/timeHelpers';

type ActivityDetailsCardProps = {
  userId: string;
  activityId?: string | null;
  externalId?: string | null;
  activityType?: ActivityLogType | null;
  activity: ActivityLog | null;
};

const ActivityDetailsCard = ({ userId, activityId, externalId, activityType, activity }: ActivityDetailsCardProps) => {
  const [details, setDetails] = useState<ActivityDetails>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const id = activityId || externalId;
  const shouldFetchDetails = !!id && (isSupportedType(activityType) && !activity?.actionType);

  const { roles } = useSelector((state: RootStateOrAny) => state.getIn(['app', 'authentication', 'user']).toJS()); // FIXME replace to typing store
  const isMentor = mentorRoles.some((role) => roles.includes(role));
  const isSupervisor = supervisorRoles.some((role) => roles.includes(role));
  const canAccessOpenRecord = isMentor || isSupervisor;

  useEffect(() => {
    if (!shouldFetchDetails) {
      setDetails(undefined);
      setError(undefined);
      setLoading(false);
      return undefined;
    }

    const loadDetail = async () => {
      setDetails(undefined);
      setError(undefined);
      setLoading(true);

      try {
        const activityDetails: ActivityDetails = await getUserActivityDetail(id, activityType);
        setDetails(activityDetails);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Could not load the activity details.');
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [activityType, id, shouldFetchDetails, userId]);

  if (!activity) {
    return (
      <DetailCard>
        <EmptyDetail>
          <strong>Select an activity</strong>
          <span>Pick a row to see the details recorded for that activity type.</span>
        </EmptyDetail>
      </DetailCard>
    );
  }

  const displayType = activity.actionType || activity.type;
  const Icon = getActivityTypeIcon(displayType);

  if (shouldFetchDetails && loading) {
    return (
      <DetailCard>
        <EmptyDetail>
          <strong>Loading activity</strong>
          <span>Fetching the complete details for this activity…</span>
        </EmptyDetail>
      </DetailCard>
    );
  }

  if (!shouldFetchDetails || error || !details) {
    const formattedDate = activity.startDate ? getEventDayAndTime(activity.startDate) : undefined;

    return (
      <DetailCard>
        <DetailHeader>
          <DetailType>
            <TypeIcon>
              <Icon />
            </TypeIcon>
            {getActivityTypeLabel(displayType)}
          </DetailType>
          <DetailTitle>{activity.details}</DetailTitle>
          <DetailMeta>
            {activity.status && <StatusPill tone={getStatusTone(activity.status)}>{activity.status}</StatusPill>}
            {formattedDate ? (
              <span>
                {formattedDate.day} · {formattedDate.time}
              </span>
            ) : null}
          </DetailMeta>
        </DetailHeader>
        <DetailFields>
          {activity.initiator && (
            <div>
              <dt>Initiator</dt>
              <dd>
                {activity.initiator.fullName} · {getInitiatorRole(activity.initiator.roles || [])}
              </dd>
            </div>
          )}
          {!!activity.duration && (
            <div>
              <dt>Duration</dt>
              <dd>{formatDuration(activity.duration)}</dd>
            </div>
          )}
        </DetailFields>
        {canOpenRecord(activity) && canAccessOpenRecord && (
          <DetailActions>
            <OpenRecordButton to={getActivityRoute(activity, userId)}>Open record</OpenRecordButton>
          </DetailActions>
        )}
      </DetailCard>
    );
  }

  const formattedDate = details.date ? getEventDayAndTime(details.date) : undefined;
  const duration = getDuration(details.date, details.passedAt);

  return (
    <DetailCard>
      <DetailHeader>
        <DetailType>
          <TypeIcon>
            <Icon />
          </TypeIcon>
          {getActivityTypeLabel(displayType)}
        </DetailType>
        <DetailTitle>{details.name}</DetailTitle>
        <DetailMeta>
          {details.status && <StatusPill tone={getStatusTone(details.status)}>{details.status}</StatusPill>}
          {formattedDate ? (
            <span>
              {formattedDate.day} · {formattedDate.time}
            </span>
          ) : null}
        </DetailMeta>
      </DetailHeader>
      <DetailFields>
        {!!duration && (
          <div>
            <dt>Duration</dt>
            <dd>{duration}</dd>
          </div>
        )}
        {!!details.course && (
          <div>
            <dt>Course</dt>
            <dd>{details.course}</dd>
          </div>
        )}
        {!!details.skill && (
          <div>
            <dt>Level</dt>
            <dd>{details.skill}</dd>
          </div>
        )}
        {activityType && hasFeedbackRecord(activityType) && (
          <div>
            <dt>Feedback</dt>
            <dd>
              {details.isFeedbackDismissed ? (
                'Feedback dismissed'
              ) : (
                <FeedbackValue hasFeedback={details.hasFeedback}>
                  {details.hasFeedback ? <CheckIcon /> : <CloseIcon />}
                </FeedbackValue>
              )}
            </dd>
          </div>
        )}
      </DetailFields>
      {canOpenRecord(activity) && canAccessOpenRecord && (
        <DetailActions>
          <OpenRecordButton to={getActivityRoute(activity, userId)}>Open record</OpenRecordButton>
        </DetailActions>
      )}
    </DetailCard>
  );
};

export default ActivityDetailsCard;
